/**
 * Board Meeting Wizard
 *
 * A 3-step wizard for creating board meeting protocols (styrelseprotokoll)
 * according to ABL 8 kap 24-25§.
 *
 * Features:
 * - Auto-numbering of protocols in sequence (format: "1/2026", "2/2026")
 * - Flexible agenda items with standard and custom items
 * - Support for adjuncts (non-board members attending specific items)
 * - Previous protocol reference and approval
 *
 * Steps:
 * 1. Meeting Details - Date, time, place, attendees (board members)
 * 2. Agenda & Decisions - Agenda items with decisions
 * 3. Review & Save
 */

import { useState, useEffect, useCallback } from "react";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  FileText,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  GripVertical,
  CalendarDays,
  Hash,
} from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import {
  protocolsApi,
  type BoardMeetingPrefillData,
  type BoardMeetingAttendee,
  type BoardMeetingDecisions,
  type BoardMeetingAgendaItem,
  type CreateBoardMeetingData,
} from "@/lib/api";

interface BoardMeetingWizardProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  onSuccess?: (protocolId: string) => void;
}

type WizardStep = 1 | 2 | 3;

interface MeetingDetails {
  meetingDate: string;
  meetingTime: string;
  meetingEndTime: string;
  meetingPlace: string;
  secretaryName: string;
  adjusterName: string;
}

export function BoardMeetingWizard({
  isOpen,
  onClose,
  companyId,
  onSuccess,
}: BoardMeetingWizardProps) {
  const [step, setStep] = useState<WizardStep>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [prefillData, setPrefillData] = useState<BoardMeetingPrefillData | null>(null);

  // Step 1: Meeting Details
  const [meetingDetails, setMeetingDetails] = useState<MeetingDetails>({
    meetingDate: new Date().toISOString().split("T")[0],
    meetingTime: "",
    meetingEndTime: "",
    meetingPlace: "",
    secretaryName: "",
    adjusterName: "",
  });

  // Board member attendees
  const [attendees, setAttendees] = useState<BoardMeetingAttendee[]>([]);

  // Step 2: Decisions
  const [decisions, setDecisions] = useState<BoardMeetingDecisions>({
    agendaItems: [],
    previousProtocol: { approved: true },
    nextMeeting: { scheduled: false },
  });

  // Load prefill data
  useEffect(() => {
    if (!isOpen) return;

    const loadPrefillData = async () => {
      setIsLoading(true);
      try {
        const response = await protocolsApi.getBoardMeetingPrefill(companyId);
        if (response.success && response.data) {
          setPrefillData(response.data);

          // Pre-fill meeting place
          setMeetingDetails((prev) => ({
            ...prev,
            meetingPlace: response.data!.company.seatMunicipality || "",
          }));

          // Pre-fill attendees from board members
          const boardAttendees: BoardMeetingAttendee[] = response.data.boardMembers.map((m) => ({
            name: m.name,
            role: m.role as BoardMeetingAttendee["role"],
            roleTitle: m.roleTitle,
            present: true,
          }));
          setAttendees(boardAttendees);

          // Pre-fill agenda items
          setDecisions((prev) => ({
            ...prev,
            agendaItems: response.data!.defaultAgendaItems,
            previousProtocol: response.data!.previousProtocol
              ? {
                  approved: true,
                  protocolNumber: response.data!.previousProtocol.protocolNumber,
                  date: response.data!.previousProtocol.meetingDate,
                }
              : { approved: true },
          }));
        }
      } catch (error) {
        console.error("Failed to load prefill data:", error);
        toast.error("Kunde inte ladda data");
      } finally {
        setIsLoading(false);
      }
    };

    loadPrefillData();
  }, [isOpen, companyId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setMeetingDetails({
        meetingDate: new Date().toISOString().split("T")[0],
        meetingTime: "",
        meetingEndTime: "",
        meetingPlace: "",
        secretaryName: "",
        adjusterName: "",
      });
      setAttendees([]);
      setDecisions({
        agendaItems: [],
        previousProtocol: { approved: true },
        nextMeeting: { scheduled: false },
      });
    }
  }, [isOpen]);

  const validateStep1 = useCallback((): boolean => {
    if (!meetingDetails.meetingDate) {
      toast.error("Ange mötesdatum");
      return false;
    }
    const presentAttendees = attendees.filter((a) => a.present);
    if (presentAttendees.length === 0) {
      toast.error("Minst en styrelseledamot måste vara närvarande");
      return false;
    }
    // Find chairman
    const chairman = presentAttendees.find((a) => a.role === "chairman");
    if (!chairman) {
      // Check if any present member can act as chairman
      const nonChairmanPresent = presentAttendees.some((a) => a.role !== "chairman");
      if (!nonChairmanPresent || presentAttendees.length < 1) {
        toast.error("Ordföranden eller en annan ledamot måste vara närvarande");
        return false;
      }
    }
    return true;
  }, [meetingDetails, attendees]);

  const validateStep2 = useCallback((): boolean => {
    if (decisions.agendaItems.length === 0) {
      toast.error("Lägg till minst en dagordningspunkt");
      return false;
    }
    // Check that all items have titles
    const emptyTitleItem = decisions.agendaItems.find((item) => !item.title || item.title.trim() === "");
    if (emptyTitleItem) {
      toast.error(`Dagordningspunkt § ${emptyTitleItem.itemNumber} saknar rubrik`);
      return false;
    }
    return true;
  }, [decisions]);

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step < 3) setStep((step + 1) as WizardStep);
  };

  const handleBack = () => {
    if (step > 1) setStep((step - 1) as WizardStep);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Find the acting chairman (the one who opens the meeting)
      const presentAttendees = attendees.filter((a) => a.present);
      const chairman = presentAttendees.find((a) => a.role === "chairman");
      const actingChairman = chairman || presentAttendees[0];

      const data: CreateBoardMeetingData = {
        meetingDate: meetingDetails.meetingDate,
        meetingTime: meetingDetails.meetingTime || undefined,
        meetingEndTime: meetingDetails.meetingEndTime || undefined,
        meetingPlace: meetingDetails.meetingPlace || undefined,
        chairpersonName: actingChairman?.name || "",
        secretaryName: meetingDetails.secretaryName || undefined,
        adjusterName: meetingDetails.adjusterName || undefined,
        attendees: attendees,
        decisions: decisions,
      };

      const response = await protocolsApi.createBoardMeeting(companyId, data);

      if (response.success && response.data) {
        toast.success(`Styrelseprotokoll ${prefillData?.nextProtocolNumber || ""} skapat!`);
        onSuccess?.(response.data.protocol.id);
        onClose();
      } else {
        toast.error(response.error?.message || "Kunde inte skapa protokoll");
      }
    } catch (error) {
      console.error("Failed to create protocol:", error);
      toast.error("Ett fel uppstod");
    } finally {
      setIsSaving(false);
    }
  };

  const stepTitles: Record<WizardStep, string> = {
    1: "Mötesuppgifter",
    2: "Dagordning",
    3: "Granska",
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-primary-600" />
          <span>Styrelseprotokoll</span>
          {prefillData && (
            <span className="text-sm font-normal text-gray-500">
              nr {prefillData.nextProtocolNumber}
            </span>
          )}
        </div>
      }
      size="2xl"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    s === step
                      ? "bg-primary-600 text-white"
                      : s < step
                        ? "bg-green-500 text-white"
                        : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {s < step ? <Check className="w-4 h-4" /> : s}
                </div>
                <span
                  className={`ml-2 text-sm font-medium ${
                    s === step ? "text-gray-900" : "text-gray-500"
                  }`}
                >
                  {stepTitles[s as WizardStep]}
                </span>
                {s < 3 && (
                  <ChevronRight
                    className={`w-5 h-5 mx-4 ${s < step ? "text-green-500" : "text-gray-300"}`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Company Info */}
          {prefillData && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-gray-900">{prefillData.company.name}</p>
                <p className="text-xs text-gray-500">Org.nr: {prefillData.company.organizationNumber}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-primary-600 flex items-center gap-1">
                  <Hash className="w-4 h-4" />
                  {prefillData.nextProtocolNumber}
                </p>
                <p className="text-xs text-gray-500">Nästa protokollnummer</p>
              </div>
            </div>
          )}

          {/* Step Content */}
          <div className="min-h-[400px]">
            {step === 1 && (
              <Step1MeetingDetails
                details={meetingDetails}
                onChange={setMeetingDetails}
                attendees={attendees}
                onAttendeesChange={setAttendees}
                prefillData={prefillData}
              />
            )}
            {step === 2 && (
              <Step2AgendaDecisions
                decisions={decisions}
                onChange={setDecisions}
                prefillData={prefillData}
              />
            )}
            {step === 3 && (
              <Step3Review
                meetingDetails={meetingDetails}
                attendees={attendees}
                decisions={decisions}
                prefillData={prefillData}
              />
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={step === 1 ? onClose : handleBack}
              className="btn btn-secondary"
              disabled={isSaving}
            >
              {step === 1 ? "Avbryt" : (
                <>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Tillbaka
                </>
              )}
            </button>

            {step < 3 ? (
              <button type="button" onClick={handleNext} className="btn btn-primary">
                Fortsätt
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="btn btn-primary flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sparar...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Skapa protokoll
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 1: Meeting Details
// ═══════════════════════════════════════════════════════════════════

interface Step1Props {
  details: MeetingDetails;
  onChange: (details: MeetingDetails) => void;
  attendees: BoardMeetingAttendee[];
  onAttendeesChange: (attendees: BoardMeetingAttendee[]) => void;
  prefillData: BoardMeetingPrefillData | null;
}

function Step1MeetingDetails({
  details,
  onChange,
  attendees,
  onAttendeesChange,
  prefillData,
}: Step1Props) {
  const updateField = (field: keyof MeetingDetails, value: string) => {
    onChange({ ...details, [field]: value });
  };

  const toggleAttendeePresent = (index: number) => {
    const updated = [...attendees];
    updated[index] = { ...updated[index], present: !updated[index].present };
    onAttendeesChange(updated);
  };

  const updateAttendeeAbsentReason = (index: number, reason: string) => {
    const updated = [...attendees];
    updated[index] = { ...updated[index], absentReason: reason };
    onAttendeesChange(updated);
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">
        Ange mötesuppgifter för styrelsesammanträdet. Protokollet kommer att numreras automatiskt
        enligt ABL 8 kap 24§.
      </p>

      {/* Previous Protocol Info */}
      {prefillData?.previousProtocol && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <FileText className="w-4 h-4 inline mr-1" />
            Föregående protokoll: nr {prefillData.previousProtocol.protocolNumber} från{" "}
            {prefillData.previousProtocol.meetingDate}
          </p>
        </div>
      )}

      {/* Date and Time */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Calendar className="w-4 h-4 inline mr-1" />
            Datum *
          </label>
          <input
            type="date"
            value={details.meetingDate}
            onChange={(e) => updateField("meetingDate", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Clock className="w-4 h-4 inline mr-1" />
            Starttid
          </label>
          <input
            type="time"
            value={details.meetingTime}
            onChange={(e) => updateField("meetingTime", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Clock className="w-4 h-4 inline mr-1" />
            Sluttid
          </label>
          <input
            type="time"
            value={details.meetingEndTime}
            onChange={(e) => updateField("meetingEndTime", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Place */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <MapPin className="w-4 h-4 inline mr-1" />
          Plats
        </label>
        <input
          type="text"
          value={details.meetingPlace}
          onChange={(e) => updateField("meetingPlace", e.target.value)}
          placeholder="T.ex. Bolagets kontor, Stockholm"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Secretary & Adjuster */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Protokollförare
          </label>
          <input
            type="text"
            value={details.secretaryName}
            onChange={(e) => updateField("secretaryName", e.target.value)}
            placeholder="Namn (valfritt)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Justerare
          </label>
          <input
            type="text"
            value={details.adjusterName}
            onChange={(e) => updateField("adjusterName", e.target.value)}
            placeholder="Namn (valfritt)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Board Members */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Users className="w-4 h-4" />
          Styrelseledamöter
        </h4>

        {attendees.length === 0 ? (
          <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Inga styrelseledamöter hittades</p>
          </div>
        ) : (
          <div className="space-y-2">
            {attendees.map((attendee, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border transition-colors ${
                  attendee.present
                    ? "bg-green-50 border-green-200"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={attendee.present}
                    onChange={() => toggleAttendeePresent(index)}
                    className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{attendee.name}</p>
                    <p className="text-xs text-gray-500">{attendee.roleTitle}</p>
                  </div>
                  {attendee.present && (
                    <Check className="w-4 h-4 text-green-600" />
                  )}
                </label>
                {!attendee.present && (
                  <div className="mt-2 ml-7">
                    <input
                      type="text"
                      value={attendee.absentReason || ""}
                      onChange={(e) => updateAttendeeAbsentReason(index, e.target.value)}
                      placeholder="Anledning till frånvaro (valfritt)"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 2: Agenda & Decisions
// ═══════════════════════════════════════════════════════════════════

interface Step2Props {
  decisions: BoardMeetingDecisions;
  onChange: (decisions: BoardMeetingDecisions) => void;
  prefillData: BoardMeetingPrefillData | null;
}

function Step2AgendaDecisions({ decisions, onChange, prefillData }: Step2Props) {
  const updateAgendaItem = (index: number, updates: Partial<BoardMeetingAgendaItem>) => {
    const newItems = [...decisions.agendaItems];
    newItems[index] = { ...newItems[index], ...updates };
    onChange({ ...decisions, agendaItems: newItems });
  };

  const addAgendaItem = () => {
    const maxNumber = Math.max(0, ...decisions.agendaItems.map((i) => i.itemNumber));
    const newItem: BoardMeetingAgendaItem = {
      itemNumber: maxNumber + 1,
      title: "",
      description: "",
      decision: "",
      itemType: "custom",
    };
    onChange({ ...decisions, agendaItems: [...decisions.agendaItems, newItem] });
  };

  const removeAgendaItem = (index: number) => {
    const newItems = decisions.agendaItems.filter((_, i) => i !== index);
    // Renumber items
    const renumbered = newItems.map((item, i) => ({ ...item, itemNumber: i + 1 }));
    onChange({ ...decisions, agendaItems: renumbered });
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">
        Redigera dagordningen och lägg till beslut för varje punkt.
      </p>

      {/* Previous Protocol Approval */}
      {prefillData?.previousProtocol && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={decisions.previousProtocol?.approved || false}
              onChange={(e) =>
                onChange({
                  ...decisions,
                  previousProtocol: {
                    ...decisions.previousProtocol,
                    approved: e.target.checked,
                    protocolNumber: prefillData.previousProtocol?.protocolNumber,
                    date: prefillData.previousProtocol?.meetingDate,
                  },
                })
              }
              className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">
                Godkänn föregående protokoll
              </span>
              <p className="text-xs text-gray-500">
                Nr {prefillData.previousProtocol.protocolNumber} från{" "}
                {prefillData.previousProtocol.meetingDate}
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Agenda Items */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900">Dagordningspunkter</h4>
          <button
            type="button"
            onClick={addAgendaItem}
            className="btn btn-sm btn-secondary flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Lägg till punkt
          </button>
        </div>

        {decisions.agendaItems.length === 0 ? (
          <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Inga dagordningspunkter</p>
            <button
              type="button"
              onClick={addAgendaItem}
              className="mt-2 text-sm text-primary-600 hover:text-primary-700"
            >
              Lägg till första punkten
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {decisions.agendaItems.map((item, index) => (
              <div
                key={index}
                className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-2 pt-2">
                    <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
                    <span className="text-sm font-bold text-primary-600">§ {item.itemNumber}</span>
                  </div>
                  <div className="flex-1 space-y-3">
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => updateAgendaItem(index, { title: e.target.value })}
                      placeholder="Rubrik på dagordningspunkten"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <textarea
                      value={item.description || ""}
                      onChange={(e) => updateAgendaItem(index, { description: e.target.value })}
                      placeholder="Beskrivning/bakgrund (valfritt)"
                      rows={2}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <textarea
                      value={item.decision || ""}
                      onChange={(e) => updateAgendaItem(index, { decision: e.target.value })}
                      placeholder="Beslut (valfritt)"
                      rows={2}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-green-50"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAgendaItem(index)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="Ta bort punkt"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Next Meeting */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <label className="flex items-center gap-3 mb-3">
          <input
            type="checkbox"
            checked={decisions.nextMeeting?.scheduled || false}
            onChange={(e) =>
              onChange({
                ...decisions,
                nextMeeting: { scheduled: e.target.checked },
              })
            }
            className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
          />
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-primary-600" />
            <span className="font-medium text-gray-900">Boka nästa möte</span>
          </div>
        </label>

        {decisions.nextMeeting?.scheduled && (
          <div className="ml-7 grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Datum</label>
              <input
                type="date"
                value={decisions.nextMeeting?.date || ""}
                onChange={(e) =>
                  onChange({
                    ...decisions,
                    nextMeeting: {
                      ...decisions.nextMeeting!,
                      date: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Tid</label>
              <input
                type="time"
                value={decisions.nextMeeting?.time || ""}
                onChange={(e) =>
                  onChange({
                    ...decisions,
                    nextMeeting: {
                      ...decisions.nextMeeting!,
                      time: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Plats</label>
              <input
                type="text"
                value={decisions.nextMeeting?.place || ""}
                onChange={(e) =>
                  onChange({
                    ...decisions,
                    nextMeeting: {
                      ...decisions.nextMeeting!,
                      place: e.target.value,
                    },
                  })
                }
                placeholder="Valfritt"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 3: Review
// ═══════════════════════════════════════════════════════════════════

interface Step3Props {
  meetingDetails: MeetingDetails;
  attendees: BoardMeetingAttendee[];
  decisions: BoardMeetingDecisions;
  prefillData: BoardMeetingPrefillData | null;
}

function Step3Review({ meetingDetails, attendees, decisions, prefillData }: Step3Props) {
  const presentAttendees = attendees.filter((a) => a.present);
  const absentAttendees = attendees.filter((a) => !a.present);

  const formatTime = (time: string): string => {
    if (!time) return "";
    return time;
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Granska uppgifterna innan du skapar protokollet.
      </p>

      {/* Protocol Number */}
      <div className="p-4 bg-primary-50 rounded-lg border border-primary-200 text-center">
        <p className="text-lg font-bold text-primary-700">
          Styrelseprotokoll nr {prefillData?.nextProtocolNumber}
        </p>
        <p className="text-sm text-primary-600">{prefillData?.company.name}</p>
      </div>

      {/* Meeting Info */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3">Mötesuppgifter</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Datum:</span>
            <span className="font-medium">{meetingDetails.meetingDate}</span>
          </div>
          {meetingDetails.meetingTime && (
            <div className="flex justify-between">
              <span className="text-gray-600">Tid:</span>
              <span className="font-medium">
                {formatTime(meetingDetails.meetingTime)}
                {meetingDetails.meetingEndTime && ` – ${formatTime(meetingDetails.meetingEndTime)}`}
              </span>
            </div>
          )}
          {meetingDetails.meetingPlace && (
            <div className="flex justify-between">
              <span className="text-gray-600">Plats:</span>
              <span className="font-medium">{meetingDetails.meetingPlace}</span>
            </div>
          )}
        </div>
      </div>

      {/* Attendees */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3">
          Närvarande ({presentAttendees.length} st)
        </h4>
        <div className="space-y-1">
          {presentAttendees.map((a, i) => (
            <p key={i} className="text-sm text-gray-700">
              {a.name} <span className="text-gray-500">({a.roleTitle})</span>
            </p>
          ))}
        </div>
        {absentAttendees.length > 0 && (
          <>
            <h4 className="font-medium text-gray-900 mt-4 mb-2">
              Frånvarande ({absentAttendees.length} st)
            </h4>
            <div className="space-y-1">
              {absentAttendees.map((a, i) => (
                <p key={i} className="text-sm text-gray-500">
                  {a.name} ({a.roleTitle})
                  {a.absentReason && <span className="italic"> – {a.absentReason}</span>}
                </p>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Agenda Items */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3">
          Dagordning ({decisions.agendaItems.length} punkter)
        </h4>
        <div className="space-y-2 text-sm">
          {decisions.agendaItems.map((item, i) => (
            <div key={i} className="flex gap-2">
              <span className="font-medium text-primary-600 w-8">§ {item.itemNumber}</span>
              <span className="text-gray-700">{item.title || "(ingen rubrik)"}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Next Meeting */}
      {decisions.nextMeeting?.scheduled && decisions.nextMeeting.date && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <CalendarDays className="w-4 h-4 inline mr-1" />
            Nästa möte: {decisions.nextMeeting.date}
            {decisions.nextMeeting.time && ` kl. ${decisions.nextMeeting.time}`}
            {decisions.nextMeeting.place && ` på ${decisions.nextMeeting.place}`}
          </p>
        </div>
      )}

      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
        <p className="text-sm text-green-800">
          <AlertCircle className="w-4 h-4 inline mr-1" />
          Efter att protokollet skapats kan du ladda ner det som PDF och redigera det tills det slutförts.
        </p>
      </div>
    </div>
  );
}

export default BoardMeetingWizard;
